/**
 * 상품정보고시 템플릿 레지스트리 (15-A-2 / ID80).
 *
 * 「전자상거래 등에서의 상품 등의 정보제공에 관한 고시」(공정거래위원회) 가 정하는
 * 품목별 필수 표시항목을 고정 상수로 보유한다. 표준 고시항목은 법령으로 정해져
 * 가맹점/상품마다 달라지지 않으므로 BE 마스터 데이터가 아니라 정적 상수가 정본이다.
 *
 * 템플릿 적용은 기존 noticeSpec(Record<string,string>) 맵에 표준 항목 키를 빈 값으로
 * 시드(prefill)할 뿐이다 — 저장/노출 경로(BE products.notice_spec, SF PDP)는 ID42 에서
 * 이미 end-to-end 구현되어 있고 본 기능은 BO 입력 편의(반복 입력 제거)만 추가한다.
 *
 * 근거: godo 공식 가이드(전자상거래 상품정보제공 고시) / 공정위 고시 표준 품목.
 */

/** 템플릿 식별 코드. noticeSpec 맵에는 저장되지 않는 BO 내부 키. */
export type NoticeSpecTemplateCode =
  | 'APPAREL'
  | 'SHOES'
  | 'BAG'
  | 'FASHION_ACCESSORY'
  | 'COSMETIC'
  | 'PROCESSED_FOOD'
  | 'HOME_GOODS'
  | 'DIGITAL_CONTENT'
  | 'GENERAL_MERCH';

export interface NoticeSpecTemplate {
  readonly code: NoticeSpecTemplateCode;
  /** BO 셀렉터에 노출되는 품목 라벨(한국어 전용). */
  readonly label: string;
  /**
   * 표준 고시항목 키 목록(표시 순서 유지). 값은 비워두고 운영자가 채운다.
   * 중복 키는 없음 — 순서 있는 고유 키 집합.
   */
  readonly keys: readonly string[];
}

/**
 * 모든 품목에 공통으로 권장되는 사업자/책임 항목. 각 템플릿 말미에 합쳐
 * "A/S 책임자 및 전화번호 / 거래조건 정보" 가 누락되지 않도록 한다.
 * 고시 표준 공통항목 정합.
 */
const COMMON_RESPONSIBILITY_KEYS: readonly string[] = [
  '제조자(수입자)',
  'A/S 책임자 및 전화번호',
];

/**
 * 품목별 표준 고시항목. 키 순서 = 폼 노출 순서.
 * (공통 책임 항목은 mergeWithCommon 으로 말미에 합산, 중복 제거.)
 */
const TEMPLATE_DEFS: readonly { code: NoticeSpecTemplateCode; label: string; keys: readonly string[] }[] = [
  {
    code: 'APPAREL',
    label: '의류',
    keys: [
      '제품 소재',
      '색상',
      '치수',
      '제조국',
      '세탁방법 및 취급 시 주의사항',
      '제조연월',
      '품질보증기준',
    ],
  },
  {
    code: 'SHOES',
    label: '구두 / 신발',
    keys: ['제품 소재', '색상', '치수', '제조국', '취급 시 주의사항', '제조연월', '품질보증기준'],
  },
  {
    code: 'BAG',
    label: '가방',
    keys: ['종류', '소재', '색상', '크기', '제조국', '취급 시 주의사항', '품질보증기준'],
  },
  {
    code: 'FASHION_ACCESSORY',
    label: '패션잡화 (모자/벨트/액세서리 등)',
    keys: ['종류', '소재', '치수', '제조국', '취급 시 주의사항', '품질보증기준'],
  },
  {
    code: 'COSMETIC',
    label: '화장품',
    keys: [
      '용량 또는 중량',
      '제품 주요 사양 (피부타입/색상 등)',
      '사용기한 또는 개봉 후 사용기간',
      '사용방법',
      '화장품제조업자 및 화장품책임판매업자',
      '제조국',
      '전성분',
      '기능성 화장품 식약처 심사필 여부',
      '사용할 때 주의사항',
    ],
  },
  {
    code: 'PROCESSED_FOOD',
    label: '가공식품',
    keys: [
      '식품의 유형',
      '생산자 및 소재지 (수입품은 수입자 및 제조국)',
      '제조연월일 / 소비기한 또는 품질유지기한',
      '포장단위별 내용물의 용량(중량)·수량',
      '원재료명 및 함량',
      '영양성분',
      '유전자변형식품 해당 여부',
      '소비자안전을 위한 주의사항',
      '수입식품 문구 여부',
    ],
  },
  {
    code: 'HOME_GOODS',
    label: '생활용품 / 가구',
    keys: ['품명 및 모델명', '크기·중량', '재질', '구성품', '제조국', '취급 시 주의사항', '품질보증기준'],
  },
  {
    code: 'DIGITAL_CONTENT',
    label: '디지털 콘텐츠 (음원/영상/이용권 등)',
    keys: [
      '제작 또는 공급자',
      '이용조건 및 이용기간',
      '상품 제공방식',
      '최소 시스템 사양 및 필수 소프트웨어',
      '청약철회 또는 계약 해지 조건',
      '소비자상담 관련 전화번호',
    ],
  },
  {
    code: 'GENERAL_MERCH',
    label: '기타 재화 (굿즈/잡화 등)',
    keys: ['품명 및 모델명', '인증·허가 사항', '제조국', '취급 시 주의사항', '품질보증기준'],
  },
];

/** 품목별 키 + 공통 책임 항목을 순서 유지로 합치고 중복 제거. */
function mergeWithCommon(keys: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const key of [...keys, ...COMMON_RESPONSIBILITY_KEYS]) {
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(key);
    }
  }
  return merged;
}

/** 셀렉터/적용에서 사용하는 정본 템플릿 목록(공통항목 합산 완료). */
export const NOTICE_SPEC_TEMPLATES: readonly NoticeSpecTemplate[] = TEMPLATE_DEFS.map((def) => ({
  code: def.code,
  label: def.label,
  keys: mergeWithCommon(def.keys),
}));

/** 코드로 템플릿 조회. 미존재 시 null. */
export function findNoticeSpecTemplate(code: string): NoticeSpecTemplate | null {
  return NOTICE_SPEC_TEMPLATES.find((tpl) => tpl.code === code) ?? null;
}

/**
 * 템플릿을 기존 noticeSpec 에 적용한 새 맵을 반환한다(불변 — 원본 미변경).
 *
 * - 템플릿 키 중 현재 맵에 없는 키만 추가한다 — 값은 seedValues 에 같은 키가 있으면 그 값,
 *   없으면 빈 값('')(D8: §1 기본정보의 원산지/제조연월/제조자를 재입력 없이 시드).
 * - 이미 값이 입력된 키는 절대 덮어쓰지 않는다(운영자 입력 보존).
 * - 운영자가 추가한 비표준 키도 그대로 보존한다.
 * - 결과 순서: 템플릿 표준 키 순서 → 그 외 기존 키.
 *
 * @returns 적용 결과 맵 + 신규로 추가된 키 수(0 이면 변화 없음).
 */
export function applyNoticeSpecTemplate(
  current: Readonly<Record<string, string>> | undefined,
  template: NoticeSpecTemplate,
  seedValues?: Readonly<Record<string, string>>,
): { next: Record<string, string>; addedCount: number } {
  const source = current ?? {};
  const next: Record<string, string> = {};
  let addedCount = 0;

  // 1) 템플릿 표준 키 순서대로 — 기존 값 보존, 신규 키는 seed 값(있으면) 또는 빈 값.
  for (const key of template.keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      next[key] = source[key] ?? '';
    } else {
      next[key] = seedValues?.[key] ?? '';
      addedCount += 1;
    }
  }

  // 2) 템플릿에 없는 운영자 추가 키 보존(원래 입력 순서 유지).
  for (const [key, value] of Object.entries(source)) {
    if (!Object.prototype.hasOwnProperty.call(next, key)) {
      next[key] = value;
    }
  }

  return { next, addedCount };
}
